import { useMutation, useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { apiClient, NotFoundError, VoicebotNotInvolvedError } from 'lib/apiClient'
import { QueryKey } from 'lib/queryClient'

import { mapDialogueSnapshot } from './mapDialogueSnapshot'
import { useLegacyRouteRedirect } from './useLegacyRouteRedirect'

export function usePromptProvider() {
  const { supportTextError, isLoadingSupportText } = useLegacyRouteRedirect()

  const [selectedProcess, setSelectedProcess] = useState<string | undefined>(undefined)

  const [searchParams] = useSearchParams()

  const correlationIds = searchParams.getAll('correlationId')
  const callId = searchParams.get('callId') ?? searchParams.get('callid')

  const [fixedPromptTextGenerations, setFixedPromptTextGenerations] = useState<
    { id: string; content: string; current: boolean }[]
  >([])

  const {
    data: debugData,
    error: debugError,
    isLoading: isLoadingDebug,
  } = useQuery({
    queryKey: [QueryKey.getAiDebug, ...correlationIds, callId],
    queryFn: () => apiClient.getAiDebug(correlationIds, callId),
    enabled: Boolean(correlationIds.length) || Boolean(callId),
  })

  useEffect(() => {
    setSelectedProcess(debugData?.debug?.[0]?.id)
  }, [debugData?.debug])

  const selectedProcessData = debugData?.debug.find(({ id }) => id === selectedProcess)

  const {
    data: dialogSnapshotData,
    error: dialogSnapshotError,
    isLoading: isLoadingDialogueSnapshot,
  } = useQuery({
    queryKey: [QueryKey.getDialogueSnapshot, ...correlationIds, callId],
    queryFn: () => apiClient.getSnapshot(correlationIds, callId, false),
    enabled: Boolean(correlationIds.length) || Boolean(callId),
  })

  const dialogueSnapshotForProcess =
    selectedProcessData &&
    dialogSnapshotData?.dialogueSnapshots.find(
      ({ correlationId }) => correlationId === selectedProcessData.correlationId,
    )

  const messageGroups = useMemo(() => {
    if (!dialogueSnapshotForProcess || !selectedProcessData) {
      return undefined
    }

    return mapDialogueSnapshot(dialogueSnapshotForProcess.messages, selectedProcessData)
  }, [selectedProcessData, dialogueSnapshotForProcess])

  const { mutate: onGenerate, isPending: isGeneratePending } = useMutation({
    mutationFn: (promptContent: string) =>
      apiClient.createFixedPromptTextGeneration({
        support_text_generation_id: selectedProcessData?.id!,
        correlation_id: uuidv4(),
        prompt_content: promptContent,
      }),
    onSuccess(data, content) {
      setFixedPromptTextGenerations(ids =>
        ids
          .map(item => ({ ...item, current: false }))
          .concat({
            id: data.fixedPromptTextGenerationId,
            content,
            current: true,
          }),
      )
    },
  })

  const onPrevClick = useCallback(() => {
    setFixedPromptTextGenerations(ids => {
      const currentIndex = ids.findIndex(item => item.current)

      if (currentIndex <= 0) {
        return ids
      }

      return ids.map((item, index) => ({ ...item, current: index === currentIndex - 1 }))
    })
  }, [])

  const onNextClick = useCallback(() => {
    setFixedPromptTextGenerations(ids => {
      const currentIndex = ids.findIndex(item => item.current)

      if (currentIndex === -1 || currentIndex === ids.length - 1) {
        return ids
      }

      return ids.map((item, index) => ({ ...item, current: index === currentIndex + 1 }))
    })
  }, [])

  const processOptions = useMemo(
    () =>
      debugData?.debug.map(({ id, label }) => ({
        label,
        value: id,
        key: id,
      })) ?? [],
    [debugData?.debug],
  )

  const customNotFoundError = useMemo(() => {
    if (isLoadingDebug || isLoadingSupportText || debugData?.debug?.length) {
      return null
    }

    if (callId) {
      return new VoicebotNotInvolvedError()
    }

    return new NotFoundError('No data found for message ID')
  }, [isLoadingDebug, isLoadingSupportText, debugData?.debug?.length, callId])

  return {
    processOptions,
    selectedProcess,
    setSelectedProcess,
    selectedProcessData,

    messageGroups,

    isLoading: isLoadingDialogueSnapshot || isLoadingDebug || isLoadingSupportText,

    // dialog snapshot may be 404 while there is debug trace data to display
    dialogSnapshotError,
    generalError: supportTextError || debugError || customNotFoundError,

    onGenerate,
    isGeneratePending,
    onPrevClick,
    onNextClick,
    currentFixedPromptTextGeneration: fixedPromptTextGenerations.find(
      item => item.current,
    ),
    currentFixedPromptTextGenerationIndex: fixedPromptTextGenerations.findIndex(
      item => item.current,
    ),
    fixedPromptTextGenerationsCount: fixedPromptTextGenerations.length,
  }
}
